import React from 'react';
import { Row, Col, Card, Typography, Tag, Space, List, Avatar } from 'antd';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import GradeOutlinedIcon from '@mui/icons-material/GradeOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { useGetClassStatsQuery } from '../../../features/courses/coursesApi';
import StatTile from '../components/StatTile';
import BarChart from '../components/BarChart';

const { Text } = Typography;

const ClassOverviewPanel = () => {
    const { data, isLoading } = useGetClassStatsQuery(undefined);

    if (isLoading || !data) {
        return <Card loading style={{ minHeight: 160 }} />;
    }

    const { students, grades, activity, turnaround, at_risk } = data;
    const iconSize = { fontSize: 19 };

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* section header */}
            <div style={{ marginBottom: -2 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--wsb-text)', lineHeight: 1.2 }}>
                    {data.course.title}
                </div>
                <Text type="secondary" style={{ fontSize: 13 }}>
                    Przegląd grupy
                </Text>
            </div>

            {/* KPI band */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 180px' }}>
                    <StatTile
                        label="Studenci"
                        value={students.enrolled}
                        hint="zapisanych na kurs"
                        accent="blue"
                        icon={<PeopleAltOutlinedIcon style={iconSize} />}
                    />
                </div>
                <div style={{ flex: '1 1 180px' }}>
                    <StatTile
                        label="Przesłane prace"
                        value={students.submitted}
                        hint={`${students.no_submission} bez pracy`}
                        accent="violet"
                        icon={<AssignmentTurnedInOutlinedIcon style={iconSize} />}
                    />
                </div>
                <div style={{ flex: '1 1 180px' }}>
                    <StatTile
                        label="Zaliczalność"
                        value={`${students.pass_rate}%`}
                        hint={`${students.passed} / ${students.enrolled} zaliczyło`}
                        accent="green"
                        icon={<VerifiedOutlinedIcon style={iconSize} />}
                    />
                </div>
                <div style={{ flex: '1 1 180px' }}>
                    <StatTile
                        label="Średnia ocena"
                        value={grades.average}
                        hint={`mediana ${grades.median}`}
                        accent="amber"
                        icon={<GradeOutlinedIcon style={iconSize} />}
                    />
                </div>
                {turnaround && (
                    <div style={{ flex: '1 1 180px' }}>
                        <StatTile
                            label="Czas oceny"
                            value={turnaround.average_days}
                            suffix="dni"
                            hint={`do ${turnaround.max_days} dni`}
                            accent="slate"
                            icon={<ScheduleOutlinedIcon style={iconSize} />}
                        />
                    </div>
                )}
            </div>

            {/* distribution + activity */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card
                        title="Rozkład ocen"
                        extra={
                            <Text type="secondary" style={{ fontSize: 12.5 }}>
                                średnia {grades.average} · mediana {grades.median} · {grades.count} ocen
                            </Text>
                        }
                        bodyStyle={{ padding: '20px 22px 14px' }}
                        style={{ height: '100%' }}
                    >
                        <BarChart
                            data={grades.distribution.map((d) => ({ label: d.grade, count: d.count }))}
                            color="#1890ff"
                            highlightMax
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card
                        title="Aktywność prac w czasie"
                        bodyStyle={{ padding: '20px 22px 14px' }}
                        style={{ height: '100%' }}
                    >
                        <BarChart data={activity} color="#52c41a" highlightMax />
                    </Card>
                </Col>
            </Row>

            {/* at-risk */}
            {at_risk && at_risk.length > 0 && (
                <Card title="Wymagają uwagi" bodyStyle={{ padding: '8px 8px' }}>
                    <List
                        grid={{ gutter: 12, xs: 1, sm: 2, lg: 2 }}
                        dataSource={at_risk}
                        renderItem={(s) => (
                            <List.Item style={{ marginBottom: 0 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        padding: '10px 14px',
                                        borderRadius: 14,
                                        background: 'var(--wsb-bg)',
                                    }}
                                >
                                    <Avatar style={{ background: '#e6f4ff', color: '#1890ff', fontWeight: 600, flexShrink: 0 }}>
                                        {(s.name || '?').slice(0, 1).toUpperCase()}
                                    </Avatar>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ fontWeight: 600, color: 'var(--wsb-text)', fontSize: 13.5 }}>
                                            {s.name}
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {s.email}
                                        </Text>
                                    </div>
                                    <Tag
                                        color={s.reason.startsWith('Brak') ? 'warning' : 'error'}
                                        style={{ borderRadius: 8, marginRight: 0, flexShrink: 0 }}
                                    >
                                        {s.reason}
                                    </Tag>
                                </div>
                            </List.Item>
                        )}
                    />
                </Card>
            )}
        </Space>
    );
};

export default ClassOverviewPanel;
